import { useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import type { ArtifactEditorFormatType } from '@/analytics/enums/artifact-editor.enums'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { resolveArtifactEditorEntryPoint, type ArtifactEditorLocationState } from '@/analytics/utils/artifact-editor-entry-analytics'
import { clearArtifactEditorSession, setArtifactEditorSession } from '@/analytics/utils/artifact-editor-session'

type UseAgentV2ArtifactEditorAnalyticsParams = {
  matterArtifactId: string | null
  matterArtifactVersionId: string | null
  conversationId: string
  matterId: string | undefined
  /** True once the editor has content on screen; the opened event waits for it. */
  isContentReady: boolean
  /** Length of the markdown currently in the editor, reported as the session's final length. */
  contentLength: number
}

/**
 * Editor-session analytics for the artifact editor.
 *
 * Observational only: this hook reads what the editor already knows and emits. It owns the
 * session id, the per-session counters, and the once-per-session guards so the editor component
 * keeps no analytics state, and it never affects loading, saving, or rendering.
 */
export function useAgentV2ArtifactEditorAnalytics({
  matterArtifactId,
  matterArtifactVersionId,
  conversationId,
  matterId,
  isContentReady,
  contentLength,
}: UseAgentV2ArtifactEditorAnalyticsParams) {
  const { trackEvent } = useAnalytics()
  const locationState = useLocation().state as ArtifactEditorLocationState | null
  const artifactEditorEntryPoint = resolveArtifactEditorEntryPoint(locationState?.artifactEditorEntryPoint)
  const sessionId = useRef<string>(uuidv4())
  const sessionStartTime = useRef<number>(Date.now())
  const hasTrackedOpened = useRef(false)
  const hasTrackedFirstEdit = useRef(false)
  const hasTrackedSessionCompleted = useRef(false)
  const saveCount = useRef(0)
  const formatActionsUsed = useRef(0)

  // The session-completed event fires from an unmount cleanup that must not re-run on every
  // change, so it reads the latest values through this snapshot rather than from a closure.
  const snapshot = useRef({ matterArtifactId, matterArtifactVersionId, conversationId, matterId, contentLength, trackEvent })
  useEffect(() => {
    snapshot.current = { matterArtifactId, matterArtifactVersionId, conversationId, matterId, contentLength, trackEvent }
  }, [contentLength, conversationId, matterArtifactId, matterArtifactVersionId, matterId, trackEvent])

  useEffect(() => {
    if (!isContentReady || !matterArtifactId || !matterArtifactVersionId || hasTrackedOpened.current) return

    hasTrackedOpened.current = true
    trackEvent(AnalyticsEvent.ArtifactEditorOpened, {
      artifactId: matterArtifactId,
      artifactVersionId: matterArtifactVersionId,
      conversationId,
      sessionId: sessionId.current,
      matterId: matterId ?? 'none',
      artifactType: 'markdown',
      artifactEditorEntryPoint,
    })
  }, [artifactEditorEntryPoint, conversationId, isContentReady, matterArtifactId, matterArtifactVersionId, matterId, trackEvent])

  // Publish the session so inline-edit analytics, which runs in a Redux listener and whose
  // review UI renders outside this subtree, can report the same editor session.
  useEffect(() => {
    const sessionIdAtMount = sessionId.current
    setArtifactEditorSession(conversationId, sessionIdAtMount)
    return () => clearArtifactEditorSession(conversationId, sessionIdAtMount)
  }, [conversationId])

  useEffect(() => {
    const sessionIdAtMount = sessionId.current
    const sessionStartTimeAtMount = sessionStartTime.current

    return () => {
      const latest = snapshot.current
      // Only a session that actually opened an artifact has something to report.
      if (!hasTrackedOpened.current || hasTrackedSessionCompleted.current) return
      if (!latest.matterArtifactId || !latest.matterArtifactVersionId) return

      hasTrackedSessionCompleted.current = true
      latest.trackEvent(AnalyticsEvent.ArtifactEditorSessionCompleted, {
        sessionDuration: Date.now() - sessionStartTimeAtMount,
        finalContentLength: latest.contentLength,
        saveCount: saveCount.current,
        formatActionsUsed: formatActionsUsed.current,
        artifactId: latest.matterArtifactId,
        artifactVersionId: latest.matterArtifactVersionId,
        conversationId: latest.conversationId,
        sessionId: sessionIdAtMount,
        matterId: latest.matterId ?? 'none',
      })
    }
  }, [])

  /**
   * Call on a user-originated document change. Emits once per editor session; the caller's
   * change handler already excludes read-only and programmatic (external) updates.
   */
  const trackFirstEdit = useCallback(() => {
    if (hasTrackedFirstEdit.current || !matterArtifactId || !matterArtifactVersionId) return

    hasTrackedFirstEdit.current = true
    trackEvent(AnalyticsEvent.ArtifactEditorFirstEdit, {
      artifactId: matterArtifactId,
      artifactVersionId: matterArtifactVersionId,
      conversationId,
      sessionId: sessionId.current,
      matterId: matterId ?? 'none',
      timeToFirstEdit: Date.now() - sessionStartTime.current,
    })
  }, [conversationId, matterArtifactId, matterArtifactVersionId, matterId, trackEvent])

  /**
   * Call when the editor persists a new version of the artifact's content.
   *
   * The saved version is passed in rather than read from the snapshot: a save creates a child
   * version and sets it as active, but that setState has not re-rendered yet when this runs, so
   * the snapshot still holds the base it was edited from. The event reports the version that now
   * holds the content.
   */
  const trackAutoSaveSuccess = useCallback(
    ({ matterArtifactVersionId: savedMatterArtifactVersionId, contentLength }: { matterArtifactVersionId: string; contentLength: number }) => {
      const latest = snapshot.current
      if (!latest.matterArtifactId) return

      saveCount.current += 1
      trackEvent(AnalyticsEvent.ArtifactEditorAutoSaveSuccess, {
        artifactId: latest.matterArtifactId,
        artifactVersionId: savedMatterArtifactVersionId,
        conversationId: latest.conversationId,
        sessionId: sessionId.current,
        matterId: latest.matterId ?? 'none',
        saveTimestamp: Date.now(),
        contentLength,
      })
    },
    [trackEvent]
  )

  /** Call when the user copies from inside the editor. */
  const trackClipboardCopy = useCallback(() => {
    const latest = snapshot.current
    if (!latest.matterArtifactId || !latest.matterArtifactVersionId) return

    trackEvent(AnalyticsEvent.ArtifactClipboardCopy, {
      artifactId: latest.matterArtifactId,
      artifactVersionId: latest.matterArtifactVersionId,
      sessionId: sessionId.current,
      ...(latest.matterId ? { matterId: latest.matterId } : {}),
    })
  }, [trackEvent])

  /** Call when a toolbar formatting command runs. */
  const trackFormattingUsed = useCallback(
    (formatType: ArtifactEditorFormatType) => {
      const latest = snapshot.current
      if (!latest.matterArtifactId || !latest.matterArtifactVersionId) return

      formatActionsUsed.current += 1
      trackEvent(AnalyticsEvent.ArtifactEditorFormattingUsed, {
        formatType,
        artifactId: latest.matterArtifactId,
        artifactVersionId: latest.matterArtifactVersionId,
        conversationId: latest.conversationId,
        sessionId: sessionId.current,
        matterId: latest.matterId ?? 'none',
      })
    },
    [trackEvent]
  )

  return { trackFirstEdit, trackAutoSaveSuccess, trackFormattingUsed, trackClipboardCopy }
}
